import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';

import { Input, Modal, SingleSelect } from '@/common';
import { Button, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import {
  AUDIO,
  COVER_PHOTO,
  IMAGE,
  PAGE_SIZE,
  PLAYLIST,
  TEXT,
  TEXT_OVERLAY,
  TRIBUTE_VIDEO,
  VIDEO
} from '@/constants';
import { AuthContext } from '@/contexts';
import { PlaylistType } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import musicThumbnail from '@/resources/img/music-thumbnail.png';
import playlistThumbnail from '@/resources/img/playlist-thumbnail.svg';
import textThumbnail from '@/resources/img/text-thumbnail.svg';
import {
  useGetAssetQuery,
  useGetAssetSlotQuery,
  useGetEventAssets,
  useUpdateAssetSlotMutation
} from '@/services';
import {
  useCreateAssetSlotEventMutation,
  useDeleteAssetSlotEventMutation,
  useUpdateAssetSlotEventMutation
} from '@/services/assetSlotEvent.service';
import { useGetAllPlaylists, useGetPlaylistById } from '@/services/playlist.service';
import { useFilterStore } from '@/store';
import {
  AssetSlot,
  AssetSlotEvent,
  AssetSlotUpdate,
  PlaylistSimple,
  SelectOptionProps
} from '@/types';

type CommandsModalProps = {
  assetSlots: AssetSlot[];
  updateAssetSlots: React.Dispatch<React.SetStateAction<AssetSlot[] | null>>;
  stopAssetSlotsChange: React.Dispatch<React.SetStateAction<boolean>>;
  selectedSlot: AssetSlot;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isModalOpen: boolean;
  refetchEvent: () => void;
};

export const CommandsModal = ({
  assetSlots,
  updateAssetSlots,
  stopAssetSlotsChange,
  selectedSlot,
  setIsModalOpen,
  isModalOpen,
  refetchEvent
}: CommandsModalProps) => {
  const [t] = useTranslation('common');
  const { eventId } = useParams();

  const [onStartEventDelete, setOnStartEventDelete] = useState<number>();
  const [whenDoneEventDelete, setWhenDoneEventDelete] = useState<number>();
  const [dontShowCeremonyTemplateEvent, setDontShowCeremonyTemplateEvent] = useState<boolean>();
  const { currentUserData: currentUser } = useContext(AuthContext);
  const { organizationFilter } = useFilterStore();
  const [searchPlaylist, setSearchPlaylist] = useState<string>('');
  const {
    data: assetSlotData,
    isLoading: isAssetSlotDataLoading,
    isFetching: isAssetsSlotDataFetching,
    refetch: refetchAssetSlotData
  } = useGetAssetSlotQuery(Number(selectedSlot?.id));

  const getOrganizationId = () => {
    if (organizationFilter && organizationFilter.value) {
      return organizationFilter.value as number;
    } else if (currentUser?.organizations.length === 1) {
      return currentUser?.organizations[0].id as number;
    }
  };

  const {
    data: allPlaylists,
    fetchNextPage: fetchNextPagePlaylist,
    hasNextPage: hasNextPagePlaylist
  } = useGetAllPlaylists(
    PAGE_SIZE,
    searchPlaylist,
    'Id',
    true,
    getOrganizationId(),
    PlaylistType.MUSIC,
    selectedSlot?.slotType?.assetType === PLAYLIST
  );

  const { data: initialPlaylist } = useGetPlaylistById(
    Number((assetSlotData?.data as AssetSlot)?.playlistId)
  );

  const getPlaylistOptions = () => {
    const options = (allPlaylists?.pages.flatMap((page) =>
      page.data?.items.map((playlist: PlaylistSimple) => ({
        value: playlist.id,
        label: playlist.name
      }))
    ) ?? []) as SelectOptionProps[];

    if (initialPlaylist && !options.some((o) => o.value === initialPlaylist.data.id)) {
      options.push({
        value: initialPlaylist.data.id,
        label: initialPlaylist.data.name
      });
    }

    return options;
  };

  const { mutate: createAssetSlotEvent, isLoading: isCreateLoading } =
    useCreateAssetSlotEventMutation(queryClient, {
      onSuccess: () => {
        refetchAssetSlotData()
          .then((res) => {
            const slotResponse = res.data;
            const slotPayload = slotResponse?.data;
            const newEvents = slotPayload?.assetSlotEvents ?? [];
            updateAssetSlots(
              (slots) =>
                slots?.map((s) =>
                  s.id === selectedSlot.id ? { ...s, assetSlotEvents: newEvents } : s
                ) ?? []
            );
          })
          .finally(() => {
            setIsModalOpen(false);
          });
      },
      onError: () => showToast(ToastType.ERROR, t('assetError'))
    });

  const { mutate: updateAssetSlotEvent, isLoading: isUpdateLoading } =
    useUpdateAssetSlotEventMutation(queryClient, {
      onSuccess: () => {
        refetchAssetSlotData();
        setIsModalOpen(false);
      },
      onError: () => {
        showToast(ToastType.ERROR, t('assetError'));
      }
    });

  const { mutate: deleteAssetSlotEvent, isLoading: isDeleteLoading } =
    useDeleteAssetSlotEventMutation(queryClient, {
      onSuccess: () => {
        refetchAssetSlotData();
        setIsModalOpen(false);
      },
      onError: () => {
        showToast(ToastType.ERROR, t('slotDeleteError'));
      }
    });

  const { mutateAsync: updateAssetSlot } = useUpdateAssetSlotMutation(queryClient, {
    onSuccess: () => {
      refetchEvent();
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const assetTypesToGet = () => {
    switch (selectedSlot?.slotType?.assetType) {
      case AUDIO:
      case PLAYLIST:
        return [COVER_PHOTO, IMAGE, VIDEO, TRIBUTE_VIDEO, TEXT_OVERLAY];
      case COVER_PHOTO:
      case IMAGE:
        return [AUDIO];
      default:
        return [];
    }
  };

  const modalLabel = () => {
    switch (selectedSlot?.slotType?.assetType) {
      case AUDIO:
        return 'song';
      case COVER_PHOTO:
        return 'coverPhoto';
      case IMAGE:
        return 'photo';
      case TEXT:
        return 'text';
      case VIDEO:
        return 'video';
      case TRIBUTE_VIDEO:
        return 'tributeVideo';
      case PLAYLIST:
        return 'playlist';
      default:
        return '';
    }
  };

  const {
    data: allCoverPhotos,
    isLoading: isAllCoverPhotosLoading,
    hasNextPage: hasNextPageCoverPhotos,
    fetchNextPage: fetchNextPageCoverPhotos
  } = useGetEventAssets(eventId as string, [], 25, undefined, [COVER_PHOTO]);

  const {
    data: allAssetsData,
    isLoading: isAllAssetsDataLoading,
    hasNextPage: hasNextPageAssets,
    fetchNextPage: fetchNextPageAssets
  } = useGetEventAssets(
    eventId as string,
    [],
    25,
    undefined,
    [IMAGE, VIDEO, TRIBUTE_VIDEO, TEXT_OVERLAY],
    undefined,
    undefined,
    !hasNextPageCoverPhotos
  );

  const filteredAssets = [
    ...(allCoverPhotos?.pages.flatMap((page) => page.data?.items) || []),
    ...(allAssetsData?.pages.flatMap((page) => page.data?.items) || [])
  ].filter((item) => assetTypesToGet().includes(item.assetType));

  const hasNextPage = hasNextPageCoverPhotos || hasNextPageAssets;
  const fetchNextPage = hasNextPageCoverPhotos ? fetchNextPageCoverPhotos : fetchNextPageAssets;

  const onStartEvent = (assetSlotData?.data as AssetSlot)?.assetSlotEvents?.find(
    (assetSlotEvent: AssetSlotEvent) => assetSlotEvent.assetSlotTrigger === 'OnStart'
  );

  const whenDoneEvent: AssetSlotEvent | undefined = (
    assetSlotData?.data as AssetSlot
  )?.assetSlotEvents?.find(
    (assetSlotEvent: AssetSlotEvent) => assetSlotEvent.assetSlotTrigger === 'WhenDone'
  );

  const { data: initialBackgroundAsset } = useGetAssetQuery(Number(onStartEvent?.value));

  const backgroundContentOptions: SelectOptionProps[] = [
    ...(filteredAssets.map((asset) => ({
      value: asset.id,
      label: asset.assetName,
      image:
        asset.assetType === AUDIO
          ? musicThumbnail
          : asset.assetType === TEXT
            ? textThumbnail
            : (asset.thumbnailUrl ?? asset.assetUrl)
    })) || []),
    ...(initialBackgroundAsset &&
    !filteredAssets?.some((a) => a.id === initialBackgroundAsset.data.id)
      ? [
          {
            value: initialBackgroundAsset.data.id,
            label: initialBackgroundAsset.data.assetName,
            image: initialBackgroundAsset.data.thumbnailUrl
          }
        ]
      : [])
  ];

  useEffect(() => {
    if (isEmpty(backgroundContentOptions) && hasNextPage) {
      fetchNextPage();
    }
  }, [backgroundContentOptions]);

  const whenDoneOptions = () => {
    const sortedAssetSlots = [...assetSlots].sort((a, b) => {
      const aIsCover = a.slotType?.assetType === COVER_PHOTO;
      const bIsCover = b.slotType?.assetType === COVER_PHOTO;

      return aIsCover === bIsCover ? 0 : aIsCover ? -1 : 1;
    });

    const whenDoneOptions: SelectOptionProps[] =
      sortedAssetSlots?.map((slot) => ({
        value: slot.id as number,
        label: (slot.name
          ? slot.name
          : slot.asset
            ? slot.asset.assetName
            : slot.playlist
              ? slot.playlist.name
              : t(slot.slotType?.name as string)) as string,
        image:
          slot?.slotType?.assetType === AUDIO
            ? musicThumbnail
            : slot?.slotType?.assetType === PLAYLIST
              ? playlistThumbnail
              : slot?.slotType?.assetType === TEXT
                ? textThumbnail
                : (slot.asset?.thumbnailUrl ?? slot.asset?.assetUrl)
      })) || [];

    const customOption: SelectOptionProps[] = [
      { value: 'StartNextAssetSlot', label: t('playNext') },
      { value: 'LoopAssetSlot', label: t('loop') }
    ];

    return customOption.concat(whenDoneOptions);
  };

  const handleCeremonyTemplateTemporaryUpdate = () => {
    stopAssetSlotsChange(true);
    const updatedSlots = assetSlots.map((slot) =>
      slot.id === selectedSlot.id
        ? {
            ...slot,
            assetSlotEvents: []
          }
        : slot
    );
    updateAssetSlots(updatedSlots);
  };

  const handleSubmit = (values: {
    name: string;
    playlistId: number | undefined;
    backgroundContent: number;
    whenDone: number | string;
  }) => {
    const payloads = [];
    const deletions = [];
    const { backgroundContent, whenDone, playlistId } = values;

    if (
      (selectedSlot.name
        ? selectedSlot.name
        : selectedSlot.asset
          ? selectedSlot.asset.assetName
          : selectedSlot.playlist
            ? selectedSlot.playlist.name
            : '') !== values.name
    ) {
      selectedSlot.name = values.name === '' ? undefined : values.name;
      updateAssetSlot(selectedSlot as AssetSlotUpdate);
    }

    if (selectedSlot.slotType?.assetType === PLAYLIST && selectedSlot.playlistId !== playlistId) {
      selectedSlot.playlistId = playlistId;
      updateAssetSlot(selectedSlot as AssetSlotUpdate);
    }

    if (dontShowCeremonyTemplateEvent) {
      handleCeremonyTemplateTemporaryUpdate();
    }

    const createPayload = (
      content: string | number,
      trigger: string,
      handler: string,
      slotEvent: AssetSlotEvent | undefined
    ) => ({
      id: slotEvent?.id,
      assetSlotId: selectedSlot?.id as number,
      assetSlotTrigger: trigger,
      assetSlotHandler: handler,
      value: content.toString()
    });

    if (backgroundContent !== -1 || (backgroundContent === -1 && onStartEvent)) {
      if (backgroundContent === -1 && onStartEvent) {
        deletions.push(onStartEventDelete);
      } else {
        payloads.push(createPayload(backgroundContent, 'OnStart', 'StartAsset', onStartEvent));
      }
    }

    const handleWhenDone = () => {
      let handler: string;
      let value: number;
      if (typeof whenDone === 'string') {
        handler = whenDone;
        value = 0;
      } else {
        handler = 'StartAssetSlot';
        value = whenDone;
      }
      return createPayload(value, 'WhenDone', handler, whenDoneEvent);
    };

    if (whenDone !== -1 || (whenDone === -1 && whenDoneEvent)) {
      handleCeremonyTemplateTemporaryUpdate();
      if (whenDone === -1 && whenDoneEvent) {
        deletions.push(whenDoneEventDelete);
      } else {
        payloads.push(handleWhenDone());
      }
    }

    payloads.forEach((payload) => {
      const isOnStart = payload?.assetSlotTrigger === 'OnStart';
      const isWhenDone = payload?.assetSlotTrigger === 'WhenDone';

      const isEventDeleted =
        (isOnStart && backgroundContent === -1) || (isWhenDone && whenDone === -1);

      let existingEvent: AssetSlotEvent | undefined;

      if (isOnStart) {
        existingEvent = onStartEvent;
      } else if (whenDoneEvent) {
        existingEvent = whenDoneEvent;
      }

      if (!isEventDeleted) {
        if (existingEvent) {
          updateAssetSlotEvent(payload);
        } else {
          createAssetSlotEvent(payload);
        }
      }
    });

    deletions.forEach((deletion) => {
      if (deletion) {
        deleteAssetSlotEvent(deletion);
      }
    });

    setIsModalOpen(false);
  };

  const initialValues = {
    name: selectedSlot.name
      ? selectedSlot.name
      : selectedSlot.asset
        ? selectedSlot.asset.assetName
        : selectedSlot.playlist
          ? selectedSlot.playlist.name
          : '',
    playlistId: selectedSlot.playlistId ?? undefined,
    backgroundContent: onStartEvent ? Number(onStartEvent.value) : -1,
    whenDone: whenDoneEvent
      ? whenDoneEvent.assetSlotHandler === 'LoopAssetSlot' ||
        whenDoneEvent?.assetSlotHandler === 'StartNextAssetSlot'
        ? whenDoneEvent.assetSlotHandler
        : whenDoneEvent.assetSlotHandler === 'StartAssetSlot'
          ? Number(whenDoneEvent.value)
          : -1
      : selectedSlot.playlistId
        ? selectedSlot.assetSlotEvents?.some((e) => e.assetSlotHandler === 'LoopAssetSlot')
          ? 'LoopAssetSlot'
          : selectedSlot.assetSlotEvents?.some((e) => e.assetSlotHandler === 'StartNextAssetSlot')
            ? 'StartNextAssetSlot'
            : -1
        : selectedSlot.slotType?.assetType === PLAYLIST
          ? 'LoopAssetSlot'
          : -1
  };

  const isLoading =
    isAllCoverPhotosLoading ||
    isAllAssetsDataLoading ||
    isUpdateLoading ||
    isCreateLoading ||
    isDeleteLoading ||
    isAssetSlotDataLoading ||
    isAssetsSlotDataFetching;

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => {
        setIsModalOpen(false);
      }}
      height='400px'
      modalClasses='w-full md:w-[490px]'
      label={t(modalLabel())}
      labelClasses='text-2xl font-bold text-primary-blue pl-2'
      containerClasses={`${isLoading && '!overflow-y-hidden'}`}>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='size-full px-2'>
          <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize={true}>
            {({ values, setFieldValue, errors, touched, dirty }) => (
              <Form className='flex flex-col gap-y-6'>
                <Input id='name' type='text' label={t('name')} placeholder={t('nameOfSlot')} />

                {[PLAYLIST].includes(selectedSlot?.slotType?.assetType as string) && (
                  <SingleSelect
                    placeholder={t('selectPlaylist')}
                    id='playlistId'
                    label={t('selectPlaylist')}
                    options={getPlaylistOptions()}
                    value={getPlaylistOptions()?.find(
                      (option) => option?.value === values.playlistId
                    )}
                    handleChange={(value) => {
                      setFieldValue('playlistId', value?.value);
                    }}
                    textColor='var(--color-secondary-gray)'
                    fetchNextPage={fetchNextPagePlaylist}
                    hasNextPage={hasNextPagePlaylist}
                    isSearchable
                    isClearable
                    handleInputChange={(inputValue: string) => setSearchPlaylist(inputValue)}
                  />
                )}
                {![VIDEO, TRIBUTE_VIDEO, IMAGE, COVER_PHOTO, TEXT].includes(
                  selectedSlot?.slotType?.assetType as string
                ) && (
                  <SingleSelect
                    placeholder={t('backgroundContent')}
                    id='backgroundContent'
                    label={t('backgroundContent')}
                    options={backgroundContentOptions}
                    value={backgroundContentOptions?.find(
                      (option) => option?.value === values.backgroundContent
                    )}
                    handleChange={(value) => {
                      const previousValue = values.backgroundContent;
                      const newValue = value?.value ?? -1;

                      const existingEvent = assetSlotData?.data?.assetSlotEvents?.find(
                        (event: AssetSlotEvent) => Number(event.value) === previousValue
                      );

                      if (newValue === -1 && existingEvent) {
                        setOnStartEventDelete(existingEvent.id);
                      }

                      setFieldValue('backgroundContent', newValue);
                    }}
                    errorMessage={errors.backgroundContent}
                    isInvalid={!!errors.backgroundContent && touched.backgroundContent}
                    textColor='var(--color-secondary-gray)'
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    isClearable
                  />
                )}
                {![IMAGE, COVER_PHOTO, TEXT].includes(
                  selectedSlot?.slotType?.assetType as string
                ) && (
                  <SingleSelect
                    placeholder={t('whenDone')}
                    id='whenDone'
                    label={t('whenDone')}
                    options={whenDoneOptions()}
                    value={whenDoneOptions()?.find((option) => option?.value === values.whenDone)}
                    handleChange={(value) => {
                      const previousValue = values.whenDone;
                      const newValue = value?.value ?? -1;
                      let existingEvent: AssetSlotEvent | undefined;
                      if (typeof previousValue === 'number') {
                        existingEvent = assetSlotData?.data?.assetSlotEvents?.find(
                          (event: AssetSlotEvent) =>
                            event.assetSlotHandler === 'StartAssetSlot' &&
                            Number(event.value) === previousValue
                        );
                      } else if (typeof previousValue === 'string') {
                        existingEvent = assetSlotData?.data?.assetSlotEvents?.find(
                          (event: AssetSlotEvent) => event.assetSlotHandler === previousValue
                        );
                      }

                      if (newValue === -1) {
                        if (existingEvent) {
                          setWhenDoneEventDelete(existingEvent.id);
                        } else {
                          setDontShowCeremonyTemplateEvent(true);
                        }
                      } else if (selectedSlot.assetSlotEvents.length > 0) {
                        setDontShowCeremonyTemplateEvent(true);
                      }

                      setFieldValue('whenDone', newValue);
                    }}
                    errorMessage={errors.whenDone}
                    isInvalid={!!errors.whenDone && touched.whenDone}
                    textColor='var(--color-secondary-gray)'
                    isClearable
                  />
                )}
                <div className='flex w-full justify-end'>
                  <Button type='submit' variant={ButtonVariant.PRIMARY} disabled={!dirty}>
                    {t('save')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </Modal>
  );
};
